import { useEffect, useRef, useState } from 'react';
import useDebounceValue from './useDebounceValue';

import { API } from '../network/api';

const useFarmerList = (query: string, country: string) => {
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState<Array<Record<string, any>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController>(new AbortController());
  const debouncedQuery = useDebounceValue(query, 500);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 1) return;

    const fetchData = async () => {
      setData(null);
      setError(null);
      setLoading(true);
      try {
        const params = {
          'name.contains': debouncedQuery,
          ...(country && { 'country.equals': country }),
          page: 0,
          size: 100,
        };
        const res = await API.get('farmers', {
          params,
          signal: abortControllerRef.current?.signal,
        });
        if (res?.data) setData(res.data);

        if (res?.data?.length === 0) setError('No data available');

        setLoading(false);
      } catch (err: any) {
        if (err?.name !== 'CanceledError') {
          const genericMsg = 'Something went wrong, Please try again';
          err?.response?.status === 500
            ? setError(genericMsg)
            : setError(err?.message || genericMsg);
          setLoading(false);
        }
      }
    };

    fetchData();

    const controller = abortControllerRef.current;
    return () => {
      controller?.abort();
      abortControllerRef.current = new AbortController();
    };
  }, [country, debouncedQuery]);

  return {
    isLoading,
    data,
    error,
  };
};

export default useFarmerList;
